import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ImageLightbox } from "../shared/ImageLightbox";
import { NotFoundPage } from "../shared/NotFoundPage";
import { useImageLightbox } from "../../hooks/useImageLightbox";
import { useLocalization } from "../../hooks/useLocalization";
import { useLocalePath } from "../../hooks/useLocalePath";
import { PageMeta } from "../../seo/PageMeta";
import { TCG_PAGE_SLUG } from "../../utils/tcgImage";
import { useTcgCards } from "./hooks/useTcgCards";
import { TcgCardGrid } from "./TcgCardGrid";
import { TcgRarityTabs, type TcgRarityFilter } from "./TcgRarityTabs";
import type { TcgRarityId } from "./types";

const TcgCollectionView = ({ slug }: { slug: string }) => {
  const { strings } = useLocalization();
  const { localized } = useLocalePath();
  const [activeRarity, setActiveRarity] = useState<TcgRarityFilter>("All");

  const rarityLabels = useMemo(
    (): Record<TcgRarityId, string> => ({
      Standard: strings.TCG.RARITY_STANDARD,
      Legendary: strings.TCG.RARITY_LEGENDARY,
      Mythic: strings.TCG.RARITY_MYTHIC,
      Ultimate: strings.TCG.RARITY_ULTIMATE,
    }),
    [strings.TCG],
  );

  const tabLabels = useMemo(
    (): Record<TcgRarityFilter, string> => ({
      All: strings.TCG.ALL,
      ...rarityLabels,
    }),
    [rarityLabels, strings.TCG.ALL],
  );

  const { cards, rarities } = useTcgCards(rarityLabels);

  const filteredCards = useMemo(() => {
    if (activeRarity === "All") {
      return cards;
    }
    return cards.filter((card) => card.rarity === activeRarity);
  }, [activeRarity, cards]);

  const lightboxItems = useMemo(
    () =>
      filteredCards.map((card) => ({
        src: card.imageUrl,
        alt: card.title,
        title: card.title,
      })),
    [filteredCards],
  );

  const lightbox = useImageLightbox(lightboxItems);

  const pathname = localized(`/tcg/${slug}`);

  return (
    <div className="min-h-svh bg-page px-[var(--padding-view)] py-[clamp(1.25rem,4vw,2rem)]">
      <PageMeta
        title={strings.TCG.INTRO_TITLE}
        description={strings.TCG.INTRO_BODY}
        pathname={pathname}
        internalPath={`/tcg/${slug}`}
        noindex
      />

      <header className="mx-auto mb-6 max-w-[48rem] text-center">
        <h1 className="m-0 font-shentox text-[clamp(1.35rem,4vw,1.85rem)] font-bold uppercase tracking-[0.06em] text-light-blue">
          {strings.TCG.INTRO_TITLE}
        </h1>
        <p className="mx-auto mt-3 max-w-[36rem] text-[0.95rem] leading-relaxed text-[#94a3b8]">
          {strings.TCG.INTRO_BODY}
        </p>
      </header>

      <div className="mx-auto max-w-[72rem]">
        {rarities.length > 0 ? (
          <div className="mb-5">
            <TcgRarityTabs
              rarities={rarities}
              active={activeRarity}
              onChange={setActiveRarity}
              labels={tabLabels}
            />
          </div>
        ) : null}

        {filteredCards.length > 0 ? (
          <TcgCardGrid
            cards={filteredCards}
            onOpenCard={(index) => {
              lightbox.open(index);
            }}
          />
        ) : (
          <p className="text-center text-[#94a3b8]">{strings.TCG.EMPTY}</p>
        )}
      </div>

      <ImageLightbox
        open={lightbox.isOpen}
        src={lightbox.activeItem?.src ?? null}
        alt={lightbox.activeItem?.alt ?? ""}
        title={lightbox.activeItem?.title ?? ""}
        onClose={lightbox.close}
        onPrev={lightbox.showPrev}
        onNext={lightbox.showNext}
        hasPrev={lightbox.hasPrev}
        hasNext={lightbox.hasNext}
      />
    </div>
  );
};

export const TcgCollectionPage = () => {
  const { slug } = useParams<{ slug: string }>();

  if (!slug || !TCG_PAGE_SLUG || slug !== TCG_PAGE_SLUG) {
    return <NotFoundPage />;
  }

  return <TcgCollectionView slug={slug} />;
};
